import React, { FC } from 'react';
import { Helmet } from 'react-helmet';
import { useUserTheme } from 'domains/user/model/selectors';
import cn from 'classnames';
import NewSelect from 'components/UI/NewSelect';
import { FormikProvider } from 'formik';
import Load from 'components/UI/Load';
import { useTranslation } from 'react-i18next';
import { ReactComponent as EmptySvg } from 'assets/errors/empty-analysis.svg';
import css from './styles.module.scss';
import Header from './components/Header';
import EmtyBlock from './components/EmtyBlock';
import LoadingBlock from './components/LoadingBlock';
import ReportTable from './components/ReportTable';
import { useReportBuilder } from './hooks/useReportBuilder';
import { Column } from './types';

const ReportBuilder: FC = () => {
  const { isThemeExtended, isThemeFull } = useUserTheme();
  const { t } = useTranslation();
  const {
    formik,
    reportsTypeList,
    isReportsTypeLoading,
    reports,
    columns,
    isReportsLoading,
    reportsListToSelectOptions,
    groupings,
    filterings,
    objections,
    statistics,
    currentUserStep,
    isGlobalLoading,
    setReportTypeId,
    setObjections,
    handleSort,
    tableSort,
    resetForm,
  } = useReportBuilder();

  return (
    <FormikProvider value={formik}>
      <div className={css.builder}>
        <Helmet>
          <title>{t(`reportbuilder.title`)}</title>
        </Helmet>
        <Header
          formik={formik}
          groupings={groupings}
          filterings={filterings}
          objections={objections}
          statistics={statistics}
          currentUserStep={currentUserStep}
          columns={columns as unknown as Column[]}
          isGlobalLoading={isGlobalLoading}
          setObjections={setObjections}
          emptyFilter={!reports?.length}
          resetForm={resetForm}
        />
        <div
          className={cn(css.content, 'container resized', {
            extend: isThemeExtended,
            full: isThemeFull,
          })}
        >
          <div className={css.selectType}>
            {isReportsTypeLoading ? (
              <Load
                style={{
                  width: 190,
                  height: 38,
                }}
              />
            ) : (
              <NewSelect
                selectAttributes={{
                  options: reportsListToSelectOptions,
                  value: formik.values.reportType,
                  hideSelectedOptions: reportsTypeList
                    ? reportsTypeList?.length <= 1
                    : false,
                }}
                onCustomChange={(val) => {
                  resetForm();
                  const reportType = reportsListToSelectOptions.find(
                    (report) => report.value === val,
                  );
                  formik.setFieldValue('reportType', reportType || null);
                  const reportTypeId = reportsTypeList?.find(
                    (report) => report.title === val,
                  )?.id;
                  if (reportTypeId) setReportTypeId(reportTypeId);
                }}
                heightCustom={32}
                viewStyle="Vertical"
                className={css.smooth}
                classNamePrefix="smooth"
              />
            )}
          </div>
          <div
            className={cn(css.body, {
              _isEmpty: !reports || !reports.length,
            })}
          >
            {isReportsLoading && <LoadingBlock />}
            {(!reports || !reports.length) &&
              (!columns || !columns.length) &&
              !isReportsLoading && <EmtyBlock />}
            {!!reports?.length && !!columns.length && !isReportsLoading && (
              <ReportTable
                columns={columns as unknown as Column[]}
                data={reports.map((report, index) => ({
                  ...report,
                  id: index,
                }))}
                handleSort={handleSort}
                tableSort={tableSort}
                statistics={statistics}
              />
            )}
            {(!reports || !reports.length) &&
              !!columns.length &&
              !isReportsLoading && (
                <div className={css.empty}>
                  <EmptySvg />
                  <span> {t(`common.empty_data`)}</span>
                </div>
              )}
          </div>
        </div>
      </div>
    </FormikProvider>
  );
};

export default ReportBuilder;
